// The company admin service (94-services-api.md, "Rules for every call"): JSON with the person's
// mail token, no cookies; a refused token is renewed and the call sent once more. Errors keep their
// `code` and status for the plain words of 02-building-blocks.md.
import type { MatrixClient } from 'matrix-js-sdk';
import { config } from '../core/config';
import { mailAccess, mailToken } from '../core/mail';
import { serverOf } from '../core/session';
import { companyEntry } from '../signin/company';

const entryOf = (baseUrl: string, userId: string) => companyEntry(config(), baseUrl) ?? companyEntry(config(), serverOf(userId));
const baseOf = (admin?: string) => (admin ? new URL(admin, location.origin).href.replace(/\/+$/, '') : undefined);

/** The person's company entry (its services). */
export const companyOf = (client: MatrixClient) => entryOf(client.baseUrl, client.getUserId()!);
/** The admin service's address for the person's company, if there is one. */
export const adminBase = (client: MatrixClient) => baseOf(companyOf(client)?.admin);

/**
 * Ends this browser's session at the company's sign-in service (94, "Sign-out at the company's
 * sign-in service"; 03, sign-out step 2): with the chat token, before the chat session ends; at
 * most 5 s, and any failure is silent.
 */
export async function signOutOfCompany(baseUrl: string, userId: string, chatToken: string | null | undefined): Promise<void> {
  const base = baseOf(entryOf(baseUrl, userId)?.admin);
  if (!base || !chatToken) return;
  const stop = new AbortController();
  const timer = setTimeout(() => stop.abort(), 5000);
  await fetch(`${base}/sign-out`, {
    method: 'POST',
    credentials: 'omit',
    cache: 'no-store',
    headers: { Authorization: `Bearer ${chatToken}` },
    signal: stop.signal,
  }).catch(() => {});
  clearTimeout(timer);
}

/** A call with the person's chat token (94, rule 3: rooms are the chat's), e.g. deleting a room. */
export async function chatAdmin<T>(client: MatrixClient, path: string, method = 'GET'): Promise<T> {
  const base = adminBase(client);
  if (!base) throw Object.assign(new Error('no admin service'), { status: 404 });
  const answer = await fetch(`${base}${path}`, {
    method,
    credentials: 'omit',
    cache: 'no-store',
    headers: { Authorization: `Bearer ${client.getAccessToken()}`, 'X-Desyr-Chat-Server': serverOf(client.getUserId()!) },
  });
  if (answer.ok) return (await answer.json()) as T;
  const body = await answer.json().catch(() => ({}));
  throw Object.assign(new Error(body.code ?? `admin ${answer.status}`), { status: answer.status, code: body.code });
}

/** In a company with mail (only then may the app call the admin service). */
export const inCompany = (client: MatrixClient) => !!adminBase(client) && !!mailAccess(client.getUserId()!);

export async function admin<T>(client: MatrixClient, path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const base = adminBase(client);
  if (!base) throw Object.assign(new Error('no admin service'), { status: 404 });
  const send = async (refused: boolean) =>
    fetch(`${base}${path}`, {
      method: init.method ?? 'GET',
      credentials: 'omit',
      cache: 'no-store',
      headers: { Authorization: `Bearer ${await mailToken(client.getUserId()!, refused)}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
      body: init.body ? JSON.stringify(init.body) : undefined,
    });
  let answer = await send(false);
  if (answer.status === 401) answer = await send(true);
  if (answer.ok) return (answer.status === 204 ? undefined : await answer.json()) as T;
  const body = await answer.json().catch(() => ({}));
  throw Object.assign(new Error(body.code ?? `admin ${answer.status}`), { status: answer.status, code: body.code, retryAfter: Number(answer.headers.get('Retry-After')) || undefined });
}
